// Reports from the class's practice sessions: what students flagged with the Report
// button, lines the platform's output check stopped, and safety concerns the AI raised.
// Open ones first; teachers resolve or dismiss them. The full review queue is Part 5.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import { ContentReport, ReportStatus, fetchClassReports, updateClassReport } from '../../services/classService';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  linkButtonStyle,
  sectionHeadingStyle,
  statusBadgeStyle,
} from './classesStyles';

const logger = createLogger('ClassReportsSection');

interface Props {
  classId: string;
  /** Teachers and school admins; grantees only look. */
  canResolve: boolean;
}

const ClassReportsSection: React.FC<Props> = ({ classId, canResolve }) => {
  const { t } = useTranslation();
  const [reports, setReports] = useState<ContentReport[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setReports(await fetchClassReports(classId));
    } catch (err) {
      logger.error('Could not load class reports', err);
      setError(t('classReports.couldNotLoad'));
    }
  }, [classId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (report: ContentReport, status: ReportStatus) => {
    setBusy(report.id);
    setError(null);
    try {
      const updated = await updateClassReport(classId, report.id, status);
      setReports(list => (list ?? []).map(r => (r.id === updated.id ? updated : r)));
    } catch (err) {
      logger.error('Could not update report', err);
      setError(errorMessage(err, t('classReports.couldNotUpdate')));
    } finally {
      setBusy(null);
    }
  };

  if (!reports) return error ? <div style={errorStyle} role="alert">{error}</div> : null;

  const ordered = [...reports].sort((a, b) => Number(b.status === 'open') - Number(a.status === 'open'));
  const open = reports.filter(r => r.status === 'open').length;

  return (
    <section id="tour-class-reports" style={cardStyle} aria-labelledby="class-reports-heading">
      <h2 id="class-reports-heading" style={sectionHeadingStyle}>
        {t('classReports.title')} {open > 0 && <span style={statusBadgeStyle(false)}>{t('classReports.openCount', { count: open })}</span>}
      </h2>
      {error && <div style={errorStyle} role="alert">{error}</div>}
      {ordered.length === 0 ? (
        <p style={{ ...hintStyle, margin: 0 }}>{t('classReports.none')}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {ordered.map(report => (
            <li key={report.id} data-testid="class-report" style={{ borderTop: '1px solid #e2e8f0', padding: '0.75rem 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '0.9rem' }}>
                  {t(`classReports.types.${report.report_type}`)}
                  <span style={{ fontWeight: 400, color: '#64748b' }}>
                    {' · '}
                    {report.reported_by === 'system' ? t('classReports.byPlatform') : report.reporter_name || t('classReports.byStudent')}
                    {' · '}
                    {new Date(report.created_at).toLocaleString(uiLocale())}
                  </span>
                </strong>
                <span style={statusBadgeStyle(report.status !== 'open')}>{t(`classReports.statuses.${report.status}`)}</span>
              </div>
              {report.message_text && <q style={{ display: 'block', fontSize: '0.85rem', margin: '6px 0' }}>{report.message_text}</q>}
              {report.details && <p style={{ ...hintStyle, margin: '4px 0' }}>{report.details}</p>}
              <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                <Link to={`/sessions/${report.roleplay_session_id}`} style={{ ...linkButtonStyle, textDecoration: 'none' }}>
                  {t('classReports.openSession')}
                </Link>
                {canResolve && report.status === 'open' && (
                  <>
                    <button type="button" style={linkButtonStyle} disabled={busy === report.id} onClick={() => setStatus(report, 'resolved')}>
                      {t('classReports.resolve')}
                    </button>
                    <button type="button" style={linkButtonStyle} disabled={busy === report.id} onClick={() => setStatus(report, 'dismissed')}>
                      {t('classReports.dismiss')}
                    </button>
                  </>
                )}
                {canResolve && report.status !== 'open' && (
                  <button type="button" style={linkButtonStyle} disabled={busy === report.id} onClick={() => setStatus(report, 'open')}>
                    {t('classReports.reopen')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ClassReportsSection;
